/**
 * TanStack Query hooks for `advertiser-sub-users`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	advertiserSubUsersQueryKeys,
	changeRole,
	invite,
	list,
	revoke,
	type InviteSubUserBody,
	type InviteSubUserResponse,
	type ListSubUsersResponse,
	type RevokeSubUserResponse,
	type SubUserRole,
	type SubUserStatus,
	type SubUserView,
} from "./api";

export function useAdvertiserSubUsersList(
	advertiserId: string,
	status?: SubUserStatus,
): UseQueryResult<ListSubUsersResponse, Error> {
	return useQuery({
		queryKey: advertiserSubUsersQueryKeys.list(advertiserId, status),
		queryFn: () => list(advertiserId, status),
		enabled: advertiserId.trim().length > 0,
	});
}

export function useAdvertiserSubUsersInvite(): UseMutationResult<
	InviteSubUserResponse,
	Error,
	InviteSubUserBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: invite,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserSubUsersQueryKeys.all });
		},
	});
}

export function useAdvertiserSubUsersRevoke(): UseMutationResult<
	RevokeSubUserResponse,
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: revoke,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserSubUsersQueryKeys.all });
		},
	});
}

export function useAdvertiserSubUsersChangeRole(): UseMutationResult<
	{ sub_user: SubUserView },
	Error,
	{ id: string; role: SubUserRole }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, role }) => changeRole(id, role),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserSubUsersQueryKeys.all });
		},
	});
}
